import React, {memo} from 'react';
import styled from 'styled-components';

import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ChannelModel} from '../../../models/api/channelModel';
import {MessageTypesEnum} from '../../../models/api/messageModel';

/*
 * Props.
 */

interface ChatComposerCounterProps {
  textLength: number;
  channel: ChannelModel;
}

/*
 * Style.
 */

const StyledCounterDiv = styled.div`
  padding: 16px 16px 0 0;
  font-variant-numeric: tabular-nums;
  display: grid;
  grid-auto-flow: row;
  justify-items: end;
`;

const SpacerDiv = styled.div`
  height: 1px;
  overflow: hidden;
  padding: 2px 0;
`;

const StyledPillDiv = styled.div`
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade80};
  background-color: ${(p) => p.theme.greys.shade30};
  border-radius: 4px;
  padding: 2px 4px;
  height: 18px;
`;

interface TweetPillStyleProps {
  $isOverLimit: boolean;
}
const StyledTweetPillDiv = styled(StyledPillDiv)<TweetPillStyleProps>`
  color: ${(p) => (p.$isOverLimit ? p.theme.palette.red.shade40 : p.theme.greys.shade80)};
  background-color: ${(p) => (p.$isOverLimit ? p.theme.palette.red.shade20 : p.theme.greys.shade30)};
`;

/*
 * Component.
 */

export const ChatComposerCounter = memo<ChatComposerCounterProps>((props) => {
  const {textLength, channel} = props;

  // Only render a counter for soft message length or tweet.
  const {softMessageLengthLimit, messageLengthLimit} = channel.behaviors;
  if (!softMessageLengthLimit && channel.messageType !== MessageTypesEnum.TWEET) {
    return null;
  }

  if (softMessageLengthLimit) {
    return (
      <StyledCounterDiv>
        {renderSMSCounter(textLength, softMessageLengthLimit)}
        <SpacerDiv aria-hidden="true">9+ SMS</SpacerDiv>
      </StyledCounterDiv>
    );
  }

  return (
    <StyledCounterDiv>
      {renderTweetCounter(textLength, messageLengthLimit)}
      <SpacerDiv aria-hidden="true">-999+</SpacerDiv>
    </StyledCounterDiv>
  );
});

/*
 * Counter variants.
 */

function renderSMSCounter(textLength: number, softMessageLengthLimit: number) {
  // Check number of SMS messages draft will split into.
  const totalMessagesToSend = Math.ceil(textLength / softMessageLengthLimit);

  if (textLength === 0) {
    return null;
  }

  if (totalMessagesToSend === 1) {
    return <StyledPillDiv>{textLength}</StyledPillDiv>;
  }

  const countText = totalMessagesToSend > 9 ? '9+' : totalMessagesToSend;
  return <StyledPillDiv>{`${countText} SMS`}</StyledPillDiv>;
}

function renderTweetCounter(textLength: number, messageLengthLimit?: number) {
  // Check if we're over the limit and render the counter.
  const lengthLimit = messageLengthLimit || 280;
  const isOverLimit = textLength > lengthLimit;
  const remainingCharacters = lengthLimit - textLength;
  const countText = remainingCharacters < -999 ? '-999+' : remainingCharacters;
  return <StyledTweetPillDiv $isOverLimit={isOverLimit}>{countText}</StyledTweetPillDiv>;
}
